import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Box, Button, TextField, Typography, Alert, Container, Chip, Stack, Avatar } from '@mui/material';
import { SearchOutlined, PersonOutlined, Warning, Link } from '@mui/icons-material';
import api from '../api';
import { uiPT, statusTextPT } from '../i18n';
import { useNavigate } from 'react-router-dom';
import Card from '../components/Card';

interface TrackingFormData {
  trackingCode: string;
}

const schema = yup.object({
  trackingCode: yup.string().required('Código de rastreamento é obrigatório').min(1, 'Código de rastreamento é obrigatório'),
});

export default function TrackingPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [client, setClient] = useState<any>(null);
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<TrackingFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      trackingCode: '',
    },
  });

  const onSubmit = async (data: TrackingFormData) => {
    setLoading(true);
    setError(null);
    setClient(null);

    try {
      const response = await api.get(`/tracking/${data.trackingCode}`);
      setClient(response.data.data);
    } catch (err: any) {
      setError(uiPT.tracking.notFound);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: (theme) => theme.palette.grey[100],
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Card sx={{ p: 4 }}>
          <Stack spacing={4} alignItems="center">
            {/* Header */}
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                  boxShadow: (theme) => theme.shadows[3],
                }}
              >
                <SearchOutlined sx={{ color: 'white', fontSize: 32 }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                {uiPT.tracking.title}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Digite seu código de rastreamento para consultar o status
              </Typography>
            </Box>

            {/* Search Form */}
            <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
              <Stack spacing={3}>
                <Controller
                  name="trackingCode"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={uiPT.tracking.trackingCode}
                      error={!!errors.trackingCode}
                      helperText={errors.trackingCode?.message}
                      disabled={loading}
                      placeholder="Digite seu código de rastreamento"
                    />
                  )}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    height: 48,
                    fontWeight: 700,
                    fontSize: '0.875rem',
                  }}
                >
                  {loading ? 'Consultando...' : uiPT.tracking.button}
                </Button>
              </Stack>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ borderRadius: 2, width: '100%' }}>
                {error}
              </Alert>
            )}

            {/* Client Information */}
            {client && (
              <Card variant="outlined" sx={{ p: 3, width: '100%' }}>
                <Stack spacing={3}>
                  {/* Client Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: 'primary.main',
                        fontSize: '1.5rem',
                        fontWeight: 700,
                      }}
                    >
                      {client.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {client.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Código: {client.trackingCode}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Status */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: 100 }}>
                      {uiPT.tracking.status}:
                    </Typography>
                    <Chip
                      label={statusTextPT[client.status] || client.status}
                      color={
                        client.status === 'ATIVA' ? 'success' :
                        client.status === 'VERIFICAÇÃO APROVADA' ? 'success' :
                        client.status === 'VERIFICAÇÃO REJEITADA' ? 'error' :
                        client.status === 'REJEITADO PELA EQUIPE' ? 'error' :
                        client.status === 'DUPLICADA' ? 'error' :
                        'warning'
                      }
                      sx={{
                        fontWeight: 600,
                        '& .MuiChip-label': {
                          px: 2,
                        },
                      }}
                    />
                  </Box>

                  {/* Show special message for CRIAR status */}
                  {client.status === 'CRIAR' && !client.rejectionReason && (
                    <Box sx={{ p: 2, bgcolor: '#e8f5e8', borderRadius: 2, border: '1px solid #4caf50' }}>
                      <Typography variant="subtitle2" color="success.dark" sx={{ fontWeight: 600, mb: 1 }}>
                        Informação:
                      </Typography>
                      <Typography variant="body2" color="success.main">
                        Seu registro está sendo analisado pela equipe. Você será notificado sobre o próximo passo do processo.
                      </Typography>
                    </Box>
                  )}

                  {/* Rejection Reason */}
                  {client.rejectionReason && (
                    <Box sx={{ p: 2, bgcolor: '#fff3cd', borderRadius: 2, border: '1px solid #ffeaa7' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Warning sx={{ color: 'warning.dark', fontSize: 20 }} />
                        <Typography variant="subtitle2" color="warning.dark" sx={{ fontWeight: 600 }}>
                          {uiPT.tracking.rejectionReason}:
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {client.rejectionReason}
                      </Typography>
                    </Box>
                  )}

                  {/* Rejected Documents */}
                  {client.rejectedDocuments && Object.keys(client.rejectedDocuments).some(key => client.rejectedDocuments![key as keyof typeof client.rejectedDocuments]) && (
                    <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 2, border: '1px solid #ef5350' }}>
                      <Typography variant="subtitle2" color="error" sx={{ fontWeight: 600, mb: 1 }}>
                        Documentos Rejeitados:
                      </Typography>
                      <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        Os seguintes documentos precisam ser reenviados:
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                        {client.rejectedDocuments.selfie && (
                          <Typography variant="body2" color="error">
                            • {uiPT.dashboard.selfie}
                          </Typography>
                        )}
                        {client.rejectedDocuments.modalityPhoto && (
                          <Typography variant="body2" color="error">
                            • {uiPT.dashboard.modalityPhoto}
                          </Typography>
                        )}
                        {client.rejectedDocuments.driverLicense && (
                          <Typography variant="body2" color="error">
                            • {uiPT.dashboard.driverLicense}
                          </Typography>
                        )}
                        {client.rejectedDocuments.idCardFront && (
                          <Typography variant="body2" color="error">
                            • {uiPT.dashboard.idCardFront}
                          </Typography>
                        )}
                        {client.rejectedDocuments.idCardBack && (
                          <Typography variant="body2" color="error">
                            • {uiPT.dashboard.idCardBack}
                          </Typography>
                        )}
                        {client.rejectedDocuments.idCardPdf && (
                          <Typography variant="body2" color="error">
                            • {uiPT.dashboard.idCardPdf}
                          </Typography>
                        )}
                        {client.rejectedDocuments.caeDocument && (
                          <Typography variant="body2" color="error">
                            • {uiPT.dashboard.caeDocument}
                          </Typography>
                        )}
                        {client.rejectedDocuments.taxClearanceDocument && (
                          <Typography variant="body2" color="error">
                            • {uiPT.dashboard.taxClearanceDocument}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate(`/resubmit/${client.trackingCode}`)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        Reenviar Documentos
                      </Button>
                    </Box>
                  )}

                  {/* Verification Link */}
                  {client.verificationLink && (
                    <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #90caf9' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Link sx={{ color: 'info.dark', fontSize: 20 }} />
                        <Typography variant="subtitle2" color="info.dark" sx={{ fontWeight: 600 }}>
                          {uiPT.tracking.verificationLink}:
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="primary">
                        <a 
                          href={client.verificationLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          {client.verificationLink}
                        </a>
                      </Typography>
                    </Box>
                  )}

                  {/* Profile Photo Upload Link */}
                  {client.profilePhotoUploadLink && (
                    <Box sx={{ p: 2, bgcolor: '#f3e5f5', borderRadius: 2, border: '1px solid #ce93d8' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PersonOutlined sx={{ color: 'secondary.dark', fontSize: 20 }} />
                        <Typography variant="subtitle2" color="secondary.dark" sx={{ fontWeight: 600 }}>
                          Link para Upload de Foto de Perfil:
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="secondary.main" sx={{ mb: 2 }}>
                        Use o link abaixo para fazer o upload da sua foto de perfil:
                      </Typography>
                      <Typography variant="body2" color="secondary">
                        <a 
                          href={client.profilePhotoUploadLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          {client.profilePhotoUploadLink}
                        </a>
                      </Typography>
                    </Box>
                  )}

                  {/* ID Document Link */}
                  {client.idDocumentLink && (
                    <Box sx={{ p: 2, bgcolor: '#e8f5e8', borderRadius: 2, border: '1px solid #81c784' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Link sx={{ color: 'success.dark', fontSize: 20 }} />
                        <Typography variant="subtitle2" color="success.dark" sx={{ fontWeight: 600 }}>
                          Link para Upload de Documento de Identidade:
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
                        Use o link abaixo para fazer o upload do seu documento de identidade:
                      </Typography>
                      <Typography variant="body2" color="success.dark">
                        <a 
                          href={client.idDocumentLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          {client.idDocumentLink}
                        </a>
                      </Typography>
                    </Box>
                  )}

                  {/* Client Details */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Tipo:</strong> {client.type === 'courier' ? 'Entregador' : 'Motorista'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Modalidade:</strong> {client.modality === 'bike' ? 'Bicicleta' : 'Motocicleta'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Cidade:</strong> {client.city}
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            )}
          </Stack>
        </Card>
      </Container>
    </Box>
  );
} 